import { useCallback, useEffect, useRef } from 'react';

import { bindAll, type UnbindFn } from 'bind-event-listener';

import { type ClientId } from '@atlassian/rovo-content-bridge-api/client-types';
import type { RovoContentBridgeSetupHandler } from '@atlassian/rovo-content-bridge-api/create-bridge-client-types';
import { useRovoContentBridgeAPI } from '@atlassian/rovo-content-bridge-api/hooks';

/**
 * Binds teardown logic to multiple window lifecycle events to ensure cleanup occurs
 * when the current JavaScript context is being destroyed (e.g., page unload, navigation).
 *
 * The provided `teardown` function will be invoked once when any of the following events fire:
 * - `beforeunload`
 * - `unload`
 * - `pagehide`
 *
 * The event listeners are automatically removed after invocation to prevent memory leaks.
 * No return value is yielded from the event handler to avoid triggering browser confirmation dialogs.
 *
 * @param teardown - A function to execute cleanup logic before the window is unloaded.
 * @returns An `UnbindFn` function that can be called to manually remove the event listeners.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/unload_event
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/pagehide_event
 */
const bindRovoBridgeTeardown = (teardown: () => void): UnbindFn => {
	const onCleanup = () => {
		teardown();
		unbind();
	};

	const unbind = bindAll(globalThis.window, [
		{
			type: 'beforeunload',
			listener: onCleanup,
			options: { once: true },
		},
		{
			type: 'unload',
			listener: onCleanup,
			options: { once: true },
		},
		{
			type: 'pagehide',
			listener: onCleanup,
			options: { once: true },
		},
	]);

	return unbind;
};

export const useRovoBridgeAPI = ({
	clientId,
	setupHandler,
}: {
	clientId: ClientId;
	setupHandler: RovoContentBridgeSetupHandler;
}): void => {
	const hasTeardownRef = useRef(false);

	const { ready, teardown } = useRovoContentBridgeAPI(clientId, setupHandler);

	const performTeardown = useCallback(() => {
		if (!hasTeardownRef.current) {
			hasTeardownRef.current = true;
			teardown();
		}
	}, [teardown]);

	// Single effect ensures deterministic ordering:
	// 1. Call ready() to initialise the bridge.
	// 2. Bind window lifecycle teardown listeners AFTER the bridge is initialised.
	// 3. Unified cleanup (component unmount) first unbinds listeners then performs teardown (idempotent).

	// This reduces chances of a race where listeners fire before initialisation completes and
	// guarantees we don't miss teardown on rapid mount/unmount cycles.
	useEffect(() => {
		const unbindRovoBridgeTeardown = bindRovoBridgeTeardown(teardown);
		ready();
		return () => {
			unbindRovoBridgeTeardown();
			performTeardown();
		};
	}, [ready, teardown, performTeardown]);
};
